import { useCallback, useState } from 'react';

/** HUD preference kept in localStorage (collapsed panels, last city tab). Storage may be unavailable, so it falls back to `initial`. */
export function usePersisted<T>(key: string, initial: T): [T, (value: T) => void] {
  const storageKey = `territile.hud.${key}`;
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === null) return initial;
      const parsed: unknown = JSON.parse(raw);
      return typeof parsed === typeof initial ? (parsed as T) : initial;
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Private mode / blocked storage: keep the in-memory value only.
      }
    },
    [storageKey]
  );
  return [value, set];
}

/** ▾/▸ button that hides or shows a HUD panel's body. */
export function CollapseToggle({
  collapsed,
  onToggle,
  label,
}: {
  collapsed: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-expanded={!collapsed}
      aria-label={`${collapsed ? 'Show' : 'Hide'} ${label}`}
      title={collapsed ? 'Show' : 'Hide'}
      onClick={onToggle}
      className="btn btn-ghost btn-icon h-6 w-6 text-xs"
    >
      {collapsed ? '▸' : '▾'}
    </button>
  );
}
